import { Grid, IconButton, Typography, useTheme } from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import { formatPhoneNumber } from '../../../utilities/formatters';
import EmailIcon from '@mui/icons-material/Email';
import {
  deleteProcureVendorContact,
  getProcureVendorById,
  getProcureVendorContactById,
  getProcureVendorContactsByProcureVendorId,
  PermissionsEnum,
  ProcureVendorContact,
  updateProcureVendorContact,
} from 'vendor-marketplace-shared-library';
import ModeEditIcon from '@mui/icons-material/ModeEdit';
import DeleteIcon from '@mui/icons-material/Delete';
import React, { useState } from 'react';
import useAuth from '../../../hooks/useAuth';
import Modal from '../../../components/common/Modal';
import ProcureVendorContactForm from '../../../components/vendors/ProcureVendorContactForm';
import { useMutation } from '@apollo/client';
import useAlert from '../../../hooks/useAlert';
import {
  DELETE_PROCURE_VENDOR_CONTACT,
  UPDATE_PROCURE_VENDOR_CONTACT,
} from '../../../error-messages';
import DeleteConfirmationModal from '../../../components/common/DeleteConfirmationModal';
import { ProcureVendorContactInput } from '../../../types/vendors';

export default function ProcureVendorContactCard(props: { contact: ProcureVendorContact }) {
  const theme = useTheme();
  const { validateUserPermissions } = useAuth();
  const alert = useAlert();
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  const [updateContact] = useMutation(updateProcureVendorContact, {
    refetchQueries: [
      getProcureVendorById,
      getProcureVendorContactsByProcureVendorId,
      getProcureVendorContactById,
    ],
    awaitRefetchQueries: true,
    onError: error => {
      alert.setAlert({
        message: error?.message ?? UPDATE_PROCURE_VENDOR_CONTACT,
      });
    },
    onCompleted: () => setOpenEditModal(false),
  });

  const [deleteContact] = useMutation(deleteProcureVendorContact, {
    refetchQueries: [getProcureVendorById, getProcureVendorContactsByProcureVendorId],
    awaitRefetchQueries: true,
    variables: {
      id: props.contact.id,
    },
    onError: error => {
      alert.setAlert({
        message: error?.message ?? DELETE_PROCURE_VENDOR_CONTACT,
      });
    },
  });

  const validateName = (fullName: string) => {
    if (!fullName.trim()) {
      alert.setAlert({ message: 'Name is required' });
      return false;
    }
    return true;
  };

  const onEdit = async (formInput: ProcureVendorContactInput) => {
    if (!validateName(formInput.fullName)) return;
    await updateContact({
      variables: {
        id: props.contact.id,
        input: formInput,
      },
    });
  };

  return (
    <Grid container direction={'column'} sx={styles.mainContainer}>
      <Grid item container sx={styles.cardContainer}>
        {/*Details */}
        <Grid item columnGap={1}>
          <Grid container>
            <Grid item>
              <Grid item>
                <Typography>{props.contact.fullName}</Typography>
              </Grid>
              <Grid item>
                <Typography sx={{ color: theme.palette.secondary.main }} variant={'caption'}>
                  {props.contact.role}
                </Typography>
              </Grid>
              {/*Phone*/}
              {props.contact.phone && (
                <Grid item container spacing={1}>
                  <Grid item>
                    <PhoneIcon fontSize={'small'} />
                  </Grid>
                  <Grid item>
                    <Typography variant={'subtitle2'}>
                      {formatPhoneNumber(props.contact.phone)}
                    </Typography>
                  </Grid>
                </Grid>
              )}
              {/*Email*/}
              {props.contact.email && (
                <Grid item container spacing={1}>
                  <Grid item>
                    <EmailIcon fontSize={'small'} />
                  </Grid>
                  <Grid item>
                    <Typography variant={'subtitle2'}>{props.contact.email}</Typography>
                  </Grid>
                </Grid>
              )}
              {props.contact.notes && (
                <Grid item>
                  <Typography variant={'caption'}>{`Notes: ${props.contact.notes}`}</Typography>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
        {/*Action buttons*/}
        {validateUserPermissions([
          PermissionsEnum.PROCURE_VENDOR_CONTACT_UPDATE,
          PermissionsEnum.PROCURE_VENDOR_CONTACT_DELETE,
        ]) && (
          <Grid item>
            <Grid container sx={styles.noWrap}>
              <Grid item>
                <IconButton onClick={() => setOpenEditModal(true)}>
                  <ModeEditIcon />
                </IconButton>
              </Grid>
              <Grid item>
                <IconButton onClick={() => setOpenDeleteModal(true)}>
                  <DeleteIcon />
                </IconButton>
              </Grid>
            </Grid>
          </Grid>
        )}
      </Grid>
      {openEditModal && (
        <Modal title={'Edit contact'} open={openEditModal} setOpen={setOpenEditModal}>
          <ProcureVendorContactForm
            vendorContactInput={{
              fullName: props.contact.fullName,
              role: props.contact.role || '',
              phone: props.contact.phone || '',
              email: props.contact.email || '',
              notes: props.contact.notes || '',
            }}
            onSubmit={onEdit}
            buttonText={'Edit contact'}
          />
        </Modal>
      )}
      {openDeleteModal && (
        <DeleteConfirmationModal
          entity={'contact'}
          open={openDeleteModal}
          setOpen={setOpenDeleteModal}
          onDelete={deleteContact}
        />
      )}
    </Grid>
  );
}

const styles = {
  mainContainer: {
    backgroundColor: 'white',
    borderRadius: 2,
    padding: 2,
  },
  cardContainer: {
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'nowrap',
  },
  noWrap: {
    flexWrap: 'nowrap',
  },
};
